import { COLOR_PALETTE, normalizeHex, readableTextOn } from '@diana/shared'

/**
 * The two looks. `site` is the original one and stays the default, so any
 * screen that has not moved to the classroom style keeps drawing exactly
 * what it drew before; `classroom` is the Studio look (style C).
 */
const LOOK = {
  site: {
    inherit: 'rounded-full border px-3 py-1 font-roboto text-[0.65rem] font-bold uppercase tracking-widest transition',
    inheritOn: 'border-gold bg-gold-100 text-gold-800',
    inheritOff: 'border-sky-200 hover:border-gold',
    swatchOn: 'ring-2 ring-ink ring-offset-2',
    own: 'flex h-7 cursor-pointer items-center gap-1.5 rounded-full border border-sky-200 px-2 transition hover:border-gold',
    ownDot: 'h-4 w-4 rounded-full border border-sky-200',
    ownText: 'font-roboto text-[0.6rem] font-bold uppercase tracking-widest text-ink-faint',
  },
  classroom: {
    inherit: 'min-h-7 cursor-pointer rounded-full border-0 px-3 font-class-text text-class-label font-semibold transition',
    inheritOn: 'bg-class-field-soft text-class-sky-700 shadow-[inset_0_0_0_1px_var(--color-class-sky-700)]',
    inheritOff: 'bg-class-white text-class-ink-soft shadow-[inset_0_0_0_1px_var(--color-class-sky-200)] hover:text-class-ink',
    swatchOn: 'ring-2 ring-class-sky-700 ring-offset-2',
    // relative: the hidden colour input is absolutely placed, and without a
    // positioned parent it hangs off the document and makes the page scroll.
    own: 'relative flex h-7 cursor-pointer items-center gap-1.5 rounded-full bg-class-white px-2.5 shadow-[inset_0_0_0_1px_var(--color-class-sky-200)] transition hover:shadow-[inset_0_0_0_1px_var(--color-class-sky-700)]',
    ownDot: 'h-4 w-4 rounded-full shadow-[inset_0_0_0_1px_rgba(0,0,0,0.12)]',
    ownText: 'text-class-label font-semibold text-class-ink-soft',
  },
}

/**
 * Twelve swatches plus the browser's own colour dialog.
 *
 * The palette is the quick path — every colour in it carries a readable
 * label, which is checked by a test — and the free picker is there because
 * Diana asked to choose her own, not to pick from a list.
 */
export function ColorPicker({
  value,
  onChange,
  /** Shown as the choice when `value` is empty. */
  fallback,
  allowInherit = false,
  inheritLabel = 'Z tagu',
  classroom = false,
}: {
  value: string
  onChange: (hex: string) => void
  fallback?: string
  allowInherit?: boolean
  inheritLabel?: string
  /** The Studio (classroom style C) look. Off by default: the old look is unchanged. */
  classroom?: boolean
}) {
  const current = normalizeHex(value) ?? ''
  const shown = current || normalizeHex(fallback ?? '') || '#b8964f'
  const look = classroom ? LOOK.classroom : LOOK.site

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {allowInherit && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-pressed={current === ''}
          title={inheritLabel}
          className={[look.inherit, current === '' ? look.inheritOn : look.inheritOff].join(' ')}
        >
          {inheritLabel}
        </button>
      )}

      {COLOR_PALETTE.map((swatch) => (
        <button
          key={swatch.hex}
          type="button"
          onClick={() => onChange(swatch.hex)}
          aria-pressed={current === swatch.hex}
          aria-label={swatch.name}
          title={swatch.name}
          style={{ backgroundColor: swatch.hex, color: readableTextOn(swatch.hex) }}
          className={[
            'flex h-7 w-7 items-center justify-center rounded-full text-[0.7rem] font-bold transition',
            current === swatch.hex ? look.swatchOn : 'hover:scale-110',
          ].join(' ')}
        >
          {current === swatch.hex ? '✓' : ''}
        </button>
      ))}

      <label title="Własny kolor" className={look.own}>
        <span aria-hidden className={look.ownDot} style={{ backgroundColor: shown }} />
        <span className={look.ownText}>Własny</span>
        <input
          type="color"
          value={shown}
          onChange={(e) => onChange(e.target.value.toLowerCase())}
          className="sr-only"
        />
      </label>
    </div>
  )
}

/** The colour of a group or a student, at a glance. */
export function ColorDot({ color, size = 10 }: { color: string; size?: number }) {
  return (
    <span
      aria-hidden
      className="inline-block shrink-0 rounded-full"
      style={{ backgroundColor: color, width: size, height: size }}
    />
  )
}
